import { useStore } from '@/containers'
import { IconSearch } from '@/styles/Icon'
import type { District, Province } from '@/types'
import { nonAccentVietnamese } from '@/utils/converter'
import { Listbox, Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import { setCookie } from 'cookies-next'
import { Fragment, useEffect, useState, type ChangeEvent } from 'react'
export interface FormAddress {
  value: string
  name: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
  classSelectButtonName?: string
  placeholder: string
  isError?: boolean | string
  isICon?: boolean
  selectContainClassName?: string
  zIndex?: string
  defaultTextColor?: string
  onDropdownOpen?: () => void
  placeholderColor?: string
  type?: string
  classNarrow?: string
  zIndexClassName?: string
  data: (Province | District)[]
}

export function FormAddress({
  placeholder,
  placeholderColor,
  classNarrow,
  name,
  onChange,
  defaultTextColor = 'text-grey-dark',
  isError = false,
  classSelectButtonName = '',
  selectContainClassName = '',
  isICon = true,
  className = '',
  data,
  type,
  value,
}: FormAddress) {
  const [selected, setSelected] = useState<
    Province | District | null | undefined
  >()
  const [searchTerm, setSearchTerm] = useState('')
  const { setRegionChange, setIsChangeRegionModal, currentRegion } = useStore()
  const filteredOptions = searchTerm
    ? data.filter(item =>
        nonAccentVietnamese(item.name).includes(nonAccentVietnamese(searchTerm))
      )
    : data
  const onListBoxChange = async (listBoxValue: Province | null | undefined) => {
    onChange({
      target: { value: listBoxValue?.code, name },
    } as ChangeEvent<HTMLInputElement>)
    setSelected(listBoxValue)
    if (
      name == 'provinceCode' &&
      listBoxValue?.saleRegionCode &&
      listBoxValue?.saleRegionCode != currentRegion &&
      type == 'cart'
    ) {
      setIsChangeRegionModal(true)
      localStorage.setItem('province', listBoxValue?.code)
      setRegionChange(listBoxValue?.saleRegionCode)
      setCookie('province', listBoxValue?.code)
      await setCookie('sale-region', listBoxValue?.saleRegionCode)
    }
    setSearchTerm('')
  }
  useEffect(() => {
    if (value && data) {
      const valueDefault = data.find(item => item.code === value)
      setSelected(valueDefault)
    }
    if (value == '') {
      setSelected(null)
    }
  }, [value, data])

  return (
    <div className={`relative ${className}`}>
      <Listbox value={selected || ''} onChange={onListBoxChange} name={name}>
        {({ open }) => (
          <>
            <Listbox.Button
              className={`flex w-full items-center justify-start border outline-none ${
                isError ? 'border-states-error focus:border-blue' : ''
              } h-12 pl-3 focus:border-blue ${classSelectButtonName} ${
                !isICon ? 'pr-3' : null
              } `}
            >
              <div
                className={`text-primary-dark  truncate ${
                  selected ? defaultTextColor : ''
                }`}
              >
                {selected ? (
                  selected?.name
                ) : (
                  <span className={placeholderColor ? placeholderColor : ''}>
                    {placeholder}
                  </span>
                )}
              </div>
              {isICon && (
                <div className={`ml-auto px-2.5 ${classNarrow}`}>
                  {open ? (
                    <IconDown className='rotate-180' />
                  ) : (
                    <IconUp className='' />
                  )}
                </div>
              )}
              <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'></span>
            </Listbox.Button>
            <Transition
              show={open}
              as={Fragment}
              leave='transition ease-in duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Listbox.Options
                className={`ring-black sm:text-sm absolute z-[9] mt-1 w-full overflow-auto rounded-md bg-white pb-1 text-base shadow-lg ring-1 ring-opacity-5 transition focus:outline-none ${selectContainClassName}`}
              >
                <div className='relative '>
                  <div className='relative z-50 w-full bg-white-400 px-1 pt-1'>
                    <div className='block items-center'>
                      <input
                        type='search'
                        name='searchAddress'
                        autoFocus={true}
                        id='searchAddress'
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.currentTarget.value)}
                        onKeyDown={e => {
                          if (e.keyCode === 32) {
                            e.stopPropagation()
                          }
                        }}
                        className='sm:text-sm block w-full rounded-md border border-gray-300 bg-gray-50 px-2 py-1 pr-12 shadow-sm outline-none transition focus:border-blue focus:outline-none focus:ring-grey-900'
                      />
                      <div className='absolute right-2 top-[60%] flex translate-y-[-50%] py-1.5 pr-1.5 '>
                        <span className=''>
                          <IconSearch />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='resultList mt-2 max-h-[200px] min-h-[120px] overflow-y-auto transition'>
                    {filteredOptions && filteredOptions.length == 0 && (
                      <p className='mt-2 text-center'>Không có dữ liệu</p>
                    )}
                    {filteredOptions &&
                      filteredOptions.length > 0 &&
                      filteredOptions.map(addItem => (
                        <Listbox.Option
                          key={addItem._id}
                          onKeyDown={e => {
                            if (e.keyCode === 32) {
                              e.stopPropagation()
                            }
                          }}
                          className={({ active, selected }) =>
                            clsx(
                              active ? 'bg-grey-900' : 'text-gray-900',
                              selected ? 'bg-grey-900' : 'text-gray-900',
                              'relative cursor-default select-none py-1 pl-3 pr-9'
                            )
                          }
                          value={addItem}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={clsx(
                                  selected ? 'font-semibold' : 'font-normal',
                                  'block truncate'
                                )}
                              >
                                {addItem.name}
                              </span>
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                  </div>
                </div>
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  )
}

export default FormAddress

interface IconUp {
  className?: string
}

function IconUp({ className = '' }: IconUp) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
interface IconDown {
  className?: string
  width?: number
  height?: number
}

function IconDown({ className = 'fill-black' }: IconDown) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
